import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createContact } from "../api/client.js";

const CONTACT_TYPES = [
  "SELLER",
  "BUYER",
  "BUYER_SELLER",
  "LEGAL",
  "AGENT",
  "DEVELOPER",
  "COMPANY",
  "INTEREST_REGISTERED"
];

function parseCsvList(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function labelForType(type) {
  return type
    .toLowerCase()
    .split("_")
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(" ");
}

export default function CrmContactCreatePage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    type: "BUYER",
    name: "",
    primaryEmail: "",
    additionalEmails: "",
    phones: "",
    tags: "",
    notes: "",
    marketingOptIn: false,
    marketingContactAllowed: false
  });

  const canSubmit = useMemo(() => form.name.trim().length > 0, [form.name]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || submitting) return;

    setSubmitting(true);
    setError("");

    try {
      const payload = {
        type: form.type,
        name: form.name.trim(),
        emails: parseCsvList(form.primaryEmail),
        additionalEmails: parseCsvList(form.additionalEmails),
        phones: parseCsvList(form.phones),
        tags: parseCsvList(form.tags),
        notes: form.notes.trim() || undefined,
        marketingOptIn: Boolean(form.marketingOptIn),
        marketingContactAllowed: Boolean(form.marketingContactAllowed),
        source: "USER_ADDED"
      };

      await createContact(payload);
      navigate("/crm/contacts", { replace: true });
    } catch (submitError) {
      setError(submitError?.message || "Failed to create contact");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <p className="text-xs uppercase tracking-[0.3em] text-slate-500">CRM contacts</p>
        <h1 className="text-3xl font-semibold text-white">Add Contact</h1>
        <p className="text-sm text-slate-400">Create a new contact and save it directly to your CRM database.</p>
      </header>

      <section className="rounded-3xl border border-white/10 bg-white/5 p-6">
        <form className="space-y-5" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2 text-sm text-slate-200">
              <span className="text-xs uppercase tracking-[0.3em] text-slate-500">Contact Type</span>
              <select
                name="type"
                value={form.type}
                onChange={handleChange}
                className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-2.5 text-white"
              >
                {CONTACT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {labelForType(type)}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-2 text-sm text-slate-200">
              <span className="text-xs uppercase tracking-[0.3em] text-slate-500">Full Name</span>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Jane Doe"
                required
                className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-2.5 text-white placeholder:text-slate-500"
              />
            </label>

            <label className="space-y-2 text-sm text-slate-200">
              <span className="text-xs uppercase tracking-[0.3em] text-slate-500">Primary Email</span>
              <input
                name="primaryEmail"
                value={form.primaryEmail}
                onChange={handleChange}
                type="email"
                placeholder="jane@example.com"
                className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-2.5 text-white placeholder:text-slate-500"
              />
            </label>

            <label className="space-y-2 text-sm text-slate-200">
              <span className="text-xs uppercase tracking-[0.3em] text-slate-500">Phone Numbers</span>
              <input
                name="phones"
                value={form.phones}
                onChange={handleChange}
                placeholder="+447700900000, 02070000000"
                className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-2.5 text-white placeholder:text-slate-500"
              />
            </label>

            <label className="space-y-2 text-sm text-slate-200 md:col-span-2">
              <span className="text-xs uppercase tracking-[0.3em] text-slate-500">Additional Emails</span>
              <input
                name="additionalEmails"
                value={form.additionalEmails}
                onChange={handleChange}
                placeholder="assistant@example.com, partner@example.com"
                className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-2.5 text-white placeholder:text-slate-500"
              />
            </label>

            <label className="space-y-2 text-sm text-slate-200 md:col-span-2">
              <span className="text-xs uppercase tracking-[0.3em] text-slate-500">Tags</span>
              <input
                name="tags"
                value={form.tags}
                onChange={handleChange}
                placeholder="vip, cash-buyer, central-london"
                className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-2.5 text-white placeholder:text-slate-500"
              />
            </label>

            <label className="space-y-2 text-sm text-slate-200 md:col-span-2">
              <span className="text-xs uppercase tracking-[0.3em] text-slate-500">Notes</span>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={4}
                placeholder="Add context about this contact..."
                className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-white placeholder:text-slate-500"
              />
            </label>
          </div>

          <div className="flex flex-wrap gap-4 text-sm text-slate-300">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                name="marketingOptIn"
                checked={form.marketingOptIn}
                onChange={handleChange}
                className="h-4 w-4 rounded border-white/20 bg-transparent"
              />
              Marketing opt-in
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                name="marketingContactAllowed"
                checked={form.marketingContactAllowed}
                onChange={handleChange}
                className="h-4 w-4 rounded border-white/20 bg-transparent"
              />
              Marketing contact allowed
            </label>
          </div>

          {error && (
            <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
              {error}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="rounded-full border border-emerald-400/40 bg-emerald-500/10 px-5 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-emerald-100 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "Saving..." : "Create contact"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/crm/contacts")}
              className="rounded-full border border-white/20 px-5 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:border-white/40"
            >
              Cancel
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}